import { LegalHeading, List, Section, SubSection } from "./layout";

export default function Privacy() {
  return (
    <>
      <title>Privacy Policy — TitanSkip</title>
      <meta
        name="description"
        content="How TitanSkip collects, uses, and protects your information when you use the skip tracing platform."
      />
      <link rel="canonical" href="https://titanskip.com/legal/privacy" />
      <meta property="og:title" content="Privacy Policy — TitanSkip" />
      <meta property="og:url" content="https://titanskip.com/legal/privacy" />

      <LegalHeading title="Privacy Policy" updated="May 14, 2026" />

      <Section title="1. Introduction">
        <p>
          This Privacy Policy describes how TitanSkip (&ldquo;we&rdquo;,
          &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, and shares
          information about you when you visit our website, create an account,
          purchase credits, or use the TitanSkip skip tracing platform
          (collectively, the &ldquo;Service&rdquo;). It also explains how we
          handle the property and contact data that we process to deliver the
          Service.
        </p>
      </Section>

      <Section title="2. Information We Collect">
        <SubSection title="Account information">
          <p>
            When you sign up, we collect the information you provide, such as
            your name, email address, company name, and billing details.
          </p>
        </SubSection>
        <SubSection title="Input data you submit">
          <p>
            To deliver the Service, you upload property addresses, parcel
            identifiers, owner names, phone numbers, and similar inputs. We
            process this data on your behalf to return matched contact records.
          </p>
        </SubSection>
        <SubSection title="Usage information">
          <p>
            We collect information about how you interact with the Service,
            including log data, IP address, device and browser information,
            referring pages, and timestamps of API calls and exports.
          </p>
        </SubSection>
        <SubSection title="Cookies and similar technologies">
          <p>
            We use a small number of strictly necessary cookies to keep you
            signed in and to remember session preferences. We do not use
            third-party advertising trackers.
          </p>
        </SubSection>
      </Section>

      <Section title="3. How We Use Information">
        <p>We use the information we collect to:</p>
        <List>
          <li>Provide, operate, and improve the Service.</li>
          <li>Process credit purchases and prevent fraudulent transactions.</li>
          <li>Communicate with you about your account, security alerts, and
            Service updates.</li>
          <li>Monitor and protect the security and integrity of the Service.</li>
          <li>Comply with applicable legal obligations and respond to valid
            legal requests.</li>
        </List>
      </Section>

      <Section title="4. Data Sources for Skip Tracing Output">
        <p>
          The contact records returned by the Service are aggregated from
          public records, commercially licensed datasets, and proprietary data
          partners. We do not collect this information from you, and we do not
          use input data you submit to enrich datasets we resell to other
          customers.
        </p>
      </Section>

      <Section title="5. How We Share Information">
        <p>We do not sell your account or input data. We share information only as follows:</p>
        <List>
          <li><span className="text-text">Service providers:</span> hosting,
            payment processing, customer support, analytics, and email delivery
            providers acting on our behalf and bound by appropriate contractual
            protections.</li>
          <li><span className="text-text">Compliance vendors:</span> our DNC,
            TCPA, and litigation list providers, who scrub the data we process
            on your behalf.</li>
          <li><span className="text-text">Legal requirements:</span> if
            required to comply with law, valid legal process, or to protect
            the rights, property, or safety of TitanSkip, our customers, or
            others.</li>
          <li><span className="text-text">Business transfers:</span> in
            connection with a merger, acquisition, financing, or sale of all or
            part of our business, subject to standard confidentiality
            protections.</li>
        </List>
      </Section>

      <Section title="6. Your Rights & Choices">
        <p>
          Depending on where you reside, you may have certain rights with
          respect to your personal information, including the right to access,
          correct, delete, or restrict the processing of your data, and the
          right to data portability.
        </p>
        <p>
          To exercise these rights, contact us at{" "}
          <a
            href="mailto:privacy@titanskip.com"
            className="text-main-300 underline-offset-4 hover:underline"
          >
            privacy@titanskip.com
          </a>
          . We will respond within the timeframe required by applicable law.
        </p>
        <SubSection title="California residents">
          <p>
            California residents have additional rights under the California
            Consumer Privacy Act (CCPA), including the right to know what
            personal information we have collected and the right to opt out of
            the sale or sharing of personal information. We do not sell or
            share personal information as defined under the CCPA.
          </p>
        </SubSection>
      </Section>

      <Section title="7. Data Retention">
        <p>
          We retain account information for as long as your account is active
          and for a reasonable period after closure as required to comply with
          legal obligations, resolve disputes, and enforce our agreements.
          Input data and the corresponding output records are retained for a
          rolling 90-day window unless a longer retention period has been
          agreed to in writing.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          We implement administrative, technical, and physical safeguards
          designed to protect your information, including encryption in
          transit and at rest, access controls, and regular security reviews.
          No system is fully secure, however, and we cannot guarantee absolute
          security.
        </p>
      </Section>

      <Section title="9. International Users">
        <p>
          The Service is operated from the United States. If you access the
          Service from outside the United States, you understand that your
          information will be transferred to, processed, and stored in the
          United States, where data protection laws may differ from those in
          your country.
        </p>
      </Section>

      <Section title="10. Children's Privacy">
        <p>
          The Service is not directed to individuals under the age of 18, and
          we do not knowingly collect personal information from children. If
          you believe we have collected information from a child, please
          contact us so we can delete it.
        </p>
      </Section>

      <Section title="11. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. If we make
          material changes, we will notify you by email or by posting a notice
          on the Service prior to the effective date. The &ldquo;Last
          updated&rdquo; date at the top of this page indicates when the
          policy was last revised.
        </p>
      </Section>

      <Section title="12. Contact">
        <p>
          For questions about this Privacy Policy or how we handle your
          information, contact us at{" "}
          <a
            href="mailto:privacy@titanskip.com"
            className="text-main-300 underline-offset-4 hover:underline"
          >
            privacy@titanskip.com
          </a>
          .
        </p>
      </Section>
    </>
  );
}
